/**
 * Universal AI Toolbar
 * 
 * A foundational design system component for the Aligned platform.
 * Every major record screen uses this exact same toolbar.
 * 
 * Platform Principle 15:
 * "Information Should Enter the Platform Naturally."
 * 
 * Toolbar Options:
 * ✏️ Type - Traditional/natural language typing
 * 🎤 Speak - Voice dictation with dual engine support
 * 📄 Upload - Document upload (PDF, Word, Excel)
 * 📷 Scan - Camera capture for licenses, cards, forms
 * 📋 Paste - Smart paste from any source
 * ✉️ Email - Email import (future)
 * 
 * Voice Mode:
 * - Standard Mode (Default): Browser Web Speech API when available
 * - Enhanced AI Mode: OpenAI Whisper for medical/care terminology
 *   Auto-activates for: Long recordings, medical terms, care plans
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './button';
import { Textarea } from './textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { 
  Pencil, Mic, Upload, Camera, ClipboardPaste, Mail,
  Loader2, X, Check, AlertCircle, FileText, Image as ImageIcon,
  MicOff, Wand2
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Input method types
const INPUT_METHODS = {
  TYPE: 'type',
  SPEAK: 'speak',
  UPLOAD: 'upload',
  SCAN: 'scan',
  PASTE: 'paste',
  EMAIL: 'email',
};

export function UniversalAIToolbar({
  entityType,
  onExtract,
  onCancel,
  isLoading = false,
  className,
  placeholder = "Type naturally... e.g., 'John Smith, born March 15, 1945, lives at 123 Main Street...'",
  showEmailOption = false,
}) {
  const [activeMethod, setActiveMethod] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [error, setError] = useState(null);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pasteContent, setPasteContent] = useState('');
  
  // Voice recording refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (recognitionRef.current) recognitionRef.current.abort();
    };
  }, []);
  
  // ============ TYPE METHOD ============
  const handleTypeSubmit = async () => {
    if (!textInput.trim()) return;
    setError(null);
    
    try {
      await onExtract({
        method: 'text',
        inputMethod: 'natural_language',
        content: textInput,
      });
    } catch (err) {
      setError(err.message || 'Extraction failed');
    }
  };
  
  // ============ VOICE METHOD ============
  const startRecording = async () => {
    setError(null);
    setTranscription('');
    audioChunksRef.current = [];
    
    try {
      // Try to use MediaRecorder for audio capture
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        
        // Convert to base64 for API
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          
          try {
            await onExtract({
              method: 'voice',
              content: base64,
              audioFormat: mediaRecorder.mimeType,
            });
          } catch (err) {
            setError(err.message || 'Voice extraction failed');
          }
        };
        reader.readAsDataURL(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      // Start timer
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
      // Also try Web Speech API for live transcription
      startLiveTranscription();
      
    } catch (err) {
      console.error('Recording error:', err);
      setError('Microphone access denied. Please allow microphone access and try again.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    }
  };
  
  const startLiveTranscription = () => {
    // Use Web Speech API for live transcription preview
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      return; // Browser doesn't support
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setTranscription(transcript);
    };
    
    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  };
  
  // ============ UPLOAD METHOD ============
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadedFile(file);
    setError(null);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setUploadPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setUploadPreview(null);
    }
  };
  
  const handleUploadSubmit = async () => {
    if (!uploadedFile) return;
    setError(null);
    
    try {
      await onExtract({
        method: 'document',
        file: uploadedFile,
      });
    } catch (err) {
      setError(err.message || 'Document extraction failed');
    }
  };
  
  // ============ SCAN METHOD ============
  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadedFile(file);
    setError(null);
    
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target.result);
    reader.readAsDataURL(file);
  };
  
  const handleScanSubmit = async () => {
    if (!uploadedFile) return;
    setError(null);
    
    try {
      await onExtract({
        method: 'image',
        file: uploadedFile,
      });
    } catch (err) {
      setError(err.message || 'Scan extraction failed');
    }
  };
  
  // ============ PASTE METHOD ============
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setPasteContent(text);
      setShowPasteDialog(true);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      setShowPasteDialog(true);
    }
  };
  
  const handlePasteSubmit = async () => {
    if (!pasteContent.trim()) return;
    setError(null);
    setShowPasteDialog(false);
    
    try {
      await onExtract({
        method: 'text',
        inputMethod: 'paste',
        content: pasteContent,
      });
    } catch (err) {
      setError(err.message || 'Paste extraction failed');
    }
  };
  
  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Reset active method
  const resetMethod = () => {
    setActiveMethod(null);
    setTextInput('');
    setTranscription('');
    setUploadedFile(null);
    setUploadPreview(null);
    setPasteContent('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };
  
  return (
    <div className={cn("space-y-4", className)} data-testid="universal-ai-toolbar">
      {/* Toolbar Buttons */}
      {!activeMethod && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveMethod(INPUT_METHODS.TYPE)}
            disabled={isLoading}
            data-testid="toolbar-type-btn"
          >
            <Pencil className="h-4 w-4 mr-2" />
            Type
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveMethod(INPUT_METHODS.SPEAK)}
            disabled={isLoading}
            data-testid="toolbar-speak-btn"
          >
            <Mic className="h-4 w-4 mr-2" />
            Speak
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setActiveMethod(INPUT_METHODS.UPLOAD);
              setTimeout(() => fileInputRef.current?.click(), 100);
            }}
            disabled={isLoading}
            data-testid="toolbar-upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setActiveMethod(INPUT_METHODS.SCAN);
              setTimeout(() => cameraInputRef.current?.click(), 100);
            }}
            disabled={isLoading}
            data-testid="toolbar-scan-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            Scan
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handlePaste}
            disabled={isLoading}
            data-testid="toolbar-paste-btn"
          >
            <ClipboardPaste className="h-4 w-4 mr-2" />
            Paste
          </Button>
          
          {showEmailOption && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveMethod(INPUT_METHODS.EMAIL)}
              disabled={isLoading}
              data-testid="toolbar-email-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </Button>
          )}
        </div>
      )}
      
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.xlsx,.txt,.csv"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        className="hidden"
        data-testid="camera-input"
      />
      
      {/* Active Method Content */}
      {activeMethod === INPUT_METHODS.TYPE && (
        <div className="space-y-3" data-testid="type-input-area">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Type naturally
            </span>
            <Button variant="ghost" size="sm" onClick={resetMethod}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <Textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={placeholder}
            rows={4}
            className="resize-none"
            data-testid="type-textarea"
          />
          
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={resetMethod}>
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={handleTypeSubmit}
              disabled={!textInput.trim() || isLoading}
              data-testid="type-extract-btn"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4 mr-2" />
              )}
              Extract
            </Button>
          </div>
        </div>
      )}
      
      {activeMethod === INPUT_METHODS.SPEAK && (
        <div className="space-y-3" data-testid="voice-input-area">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2">
              <Mic className="h-4 w-4" />
              Voice dictation
            </span>
            <Button variant="ghost" size="sm" onClick={resetMethod}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex flex-col items-center gap-4 py-4">
            {!isRecording ? (
              <Button
                size="lg"
                onClick={startRecording}
                className="rounded-full h-16 w-16"
                disabled={isLoading}
                data-testid="start-recording-btn"
              >
                <Mic className="h-8 w-8" />
              </Button>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="relative">
                  <Button
                    size="lg"
                    variant="destructive"
                    onClick={stopRecording}
                    className="rounded-full h-16 w-16 animate-pulse"
                    data-testid="stop-recording-btn"
                  >
                    <MicOff className="h-8 w-8" />
                  </Button>
                </div>
                <span className="text-lg font-mono">{formatTime(recordingTime)}</span>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              {isRecording 
                ? "Speak naturally. Tap to stop when done."
                : "Tap to start recording. AI will transcribe and extract data."}
            </p>
          </div>
          
          {/* Live transcription preview */}
          {transcription && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-muted-foreground mb-1">Live preview:</p>
              <p className="text-sm">{transcription}</p>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={resetMethod}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      
      {activeMethod === INPUT_METHODS.UPLOAD && (
        <div className="space-y-3" data-testid="upload-input-area">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload document
            </span>
            <Button variant="ghost" size="sm" onClick={resetMethod}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {uploadedFile ? (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">{uploadedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => {
                  setUploadedFile(null);
                  fileInputRef.current.value = '';
                }}>
                  Remove
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleUploadSubmit}
                  disabled={isLoading}
                  data-testid="upload-extract-btn"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  Extract
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Click to select file</p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, Word, Excel, Text, or CSV
              </p>
            </div>
          )}
        </div>
      )}
      
      {activeMethod === INPUT_METHODS.SCAN && (
        <div className="space-y-3" data-testid="scan-input-area">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Camera scan
            </span>
            <Button variant="ghost" size="sm" onClick={resetMethod}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {uploadPreview ? (
            <div className="border rounded-lg p-4 space-y-3">
              <img 
                src={uploadPreview} 
                alt="Captured" 
                className="max-h-48 mx-auto rounded"
              />
              
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => {
                  setUploadedFile(null);
                  setUploadPreview(null);
                  cameraInputRef.current.value = '';
                }}>
                  Retake
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleScanSubmit}
                  disabled={isLoading}
                  data-testid="scan-extract-btn"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  Extract
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Tap to capture</p>
              <p className="text-xs text-muted-foreground mt-1">
                Driver&apos;s license, ID card, or form
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Paste Dialog */}
      <Dialog open={showPasteDialog} onOpenChange={setShowPasteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" />
              Paste content
            </DialogTitle>
            <DialogDescription>
              Paste text from any source — emails, documents, websites, etc.
            </DialogDescription>
          </DialogHeader>
          
          <Textarea
            value={pasteContent}
            onChange={(e) => setPasteContent(e.target.value)}
            placeholder="Paste content here..."
            rows={6}
            className="resize-none"
            data-testid="paste-textarea"
          />
          
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowPasteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePasteSubmit}
              disabled={!pasteContent.trim() || isLoading}
              data-testid="paste-extract-btn"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4 mr-2" />
              )}
              Extract
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-lg">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">
            AI is extracting information...
          </span>
        </div>
      )}
    </div>
  );
}

export default UniversalAIToolbar;
